import { useEffect, useState } from "react";

import { type IAdvancedMatching } from "@/features/tracking/advancedMatching/AdvancedMatching";
import hashAdvancedMatchingField from "@/features/tracking/advancedMatching/hashAdvancedMatchingField";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import useAuth from "@/services/useAuth";

const advancedMatchingLogger = appLogger.withTag("AdvancedMatching");
const USER_FIELD_EMAIL = "email";
// Kinde-authenticated users (the majority on SpicyChat/PixelChat) do
// not carry an `external_id` field on `useAuth().user`; only partner
// users do. The backend CAPI producer sends
// `external_id = SHA-256(userData.userId)` — reading `user.id` here
// keeps the browser pixel and the server event aligned on the
// strongest non-email identifier so Meta can dedup them. See
// `hashStandardUserFields` in spicychat-backend.
const USER_FIELD_EXTERNAL_ID = "id";

function readStringField(source: unknown, fieldName: string): string | undefined {
  if (source === null || source === undefined || typeof source !== "object") {
    return undefined;
  }
  const value = (source as Record<string, unknown>)[fieldName];
  return typeof value === "string" ? value : undefined;
}

async function hashIdentity({ email, externalId }: { email: string | undefined; externalId: string | undefined }): Promise<IAdvancedMatching> {
  const [hashedEmail, hashedExternalId] = await Promise.all([hashAdvancedMatchingField(email), hashAdvancedMatchingField(externalId)]);
  const result: IAdvancedMatching = {};
  if (hashedEmail !== undefined) {
    result.email = hashedEmail;
  }
  if (hashedExternalId !== undefined) {
    result.externalId = hashedExternalId;
  }
  return result;
}

/**
 * Three-state hashed identity for ad-platform pixels.
 *
 * - `undefined` — auth is still resolving. Pixel hooks MUST wait; initializing
 *   with empty identity now means Meta sees the first PageView as anonymous
 *   and the subsequent `init` update is silently ignored.
 * - `{}` — auth has resolved, user is logged out. Pixel hooks initialize
 *   with no personal data so `PageView` still fires for acquisition tracking.
 * - `{ email, externalId }` — authenticated; hashed digests ready to attach.
 *
 * Re-computes on email / external_id change (login, logout, email update)
 * so pixel hooks can re-init downstream. Digests are memoized in state
 * across re-renders of this hook's consumers.
 */
function useAdvancedMatching(): IAdvancedMatching | undefined {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [stateMatching, setStateMatching] = useState<IAdvancedMatching | undefined>(undefined);

  const email = readStringField(user, USER_FIELD_EMAIL);
  const externalId = readStringField(user, USER_FIELD_EXTERNAL_ID);

  useEffect(() => {
    if (isAuthLoading) {
      setStateMatching(undefined);
      return;
    }
    if (email === undefined && externalId === undefined) {
      setStateMatching({});
      return;
    }

    let isCancelled = false;
    hashIdentity({ email, externalId })
      .then((next) => {
        if (!isCancelled) {
          setStateMatching(next);
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          advancedMatchingLogger.warn("hashing failed; falling back to empty matching", { error });
          setStateMatching({});
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isAuthLoading, email, externalId]);

  return stateMatching;
}

export default useAdvancedMatching;
